// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
/**
 * Facet dropdown options for the Invoices AND Quotes lists, extracted from the
 * pages so the option lists can be unit tested without mounting a table.
 *
 * ONE module for both pages because ONE controller serves both — a quote is an
 * invoice with `document_type: "quote"`, and `QUOTES_TABLE.facetKeys` is a
 * spread of `INVOICES_TABLE.facetKeys`. Splitting this in two would be two
 * places for the same key to drift.
 *
 * The keys here are HALF of a contract with `Api::V1::InvoicesController`'s
 * `filterable(...)` declaration; the other half is pinned by
 * `components/table/config-parity.test.ts` via `INVOICES_TABLE.facetKeys`, and
 * by `xop-brain/apps/crm-api/test/controllers/concerns/declared_keys_export_test.rb`
 * on the server side.
 */
import type { FacetDef } from "@crm-src/components/table/TableFilterBar";
import type { FacetKeysOf } from "@crm-src/components/table/configs";
import { INVOICES_TABLE } from "@crm-src/components/table/configs";
import { personLabel, type PersonLike } from "@crm-src/components/table/person-label";
import type { InvoiceListFilters } from "@crm-src/lib/store/invoicesSlice";

/**
 * The Invoices/Quotes facet keys, DERIVED from the shared config rather than
 * re-typed — so there is exactly one place a key can be misspelled, and
 * misspelling it there breaks the assertion below.
 */
export type InvoiceFacetKey = FacetKeysOf<typeof INVOICES_TABLE>;

/**
 * Compile-time proof that every declared facet key is a param
 * `InvoiceListFilters` actually carries — i.e. one the API implements. A key
 * that only exists in the config otherwise compiles fine and fails at RUNTIME
 * as a param the server drops, with `config-parity.test.ts` as the only net.
 */
type FacetKeysAreFilterFields =
  InvoiceFacetKey extends keyof InvoiceListFilters ? true : never;
const _facetKeysAreFilterFields: FacetKeysAreFilterFields = true;
void _facetKeysAreFilterFields;

/**
 * The four values `invoices.payment_status` can hold.
 *
 * Verified against the model, NOT against the UI: `Invoice` declares
 * `validates :payment_status, inclusion: { in: %w[unpaid partial paid refunded] }`.
 * The status BADGE components in this repo also colour `draft`, `sent`,
 * `viewed`, `partial_payment`, `overdue` and `cancelled` — none of which is a
 * value this column can actually hold (`overdue` is DERIVED from
 * `due_date < today AND payment_status != 'paid'`, and is the coarse
 * `?status=` filter's vocabulary, not this column's). Offering any of them
 * here would render a dropdown option that silently matches zero rows.
 */
const PAYMENT_STATUSES: FacetDef["options"] = [
  { value: "unpaid", label: "Unpaid" },
  { value: "partial", label: "Partial" },
  { value: "paid", label: "Paid" },
  { value: "refunded", label: "Refunded" },
];

/**
 * Build the Invoices / Quotes filter-bar facets.
 *
 * Options come from data the page ALREADY has — a fixed enum and the user
 * roster — so no endpoint exists just to populate a dropdown.
 *
 * Returns every facet the CALLER'S CONFIG declares, INCLUDING one whose options
 * came back empty (`useUsers()` answers 403 with `[]` for a non-admin):
 * dropping an optionless facet from the rendered controls is `TableFilterBar`'s
 * job, so every list page inherits it. This stays a pure function of its inputs.
 *
 * `keys` is what makes the two pages differ. `TableFilterBar` renders whatever
 * `facetDefs` it is handed and does NOT intersect them with the page's
 * `facetKeys`, so a facet dropped from a config would otherwise keep rendering.
 * The Quotes page passes `QUOTES_TABLE.facetKeys`, which omits `payment_status`
 * — a column no quote cell renders and that every UI-created quote holds the
 * `unpaid` default of. See `QUOTES_TABLE` for the full reasoning. The default
 * keeps every other caller (and the Invoices page) unchanged.
 */
export function buildInvoiceFacetDefs(
  users: PersonLike[],
  keys: readonly InvoiceFacetKey[] = INVOICES_TABLE.facetKeys,
): FacetDef<InvoiceFacetKey>[] {
  const all: FacetDef<InvoiceFacetKey>[] = [
    { key: "payment_status", label: "Payment Status", options: PAYMENT_STATUSES },
    {
      key: "created_by_id",
      label: "Created By",
      options: users.map((u) => ({
        value: String(u.id),
        label: personLabel(u),
      })),
    },
  ];
  // Declaration order is preserved (it is the on-screen order), and a key the
  // caller does not declare is not built at all.
  return all.filter((facet) => keys.includes(facet.key));
}
